/**
 * ĐỔI MẬT KHẨU — màn bắt buộc ở lần đăng nhập đầu (docs/14 §14.3 B8).
 *
 * KHÔNG có nút bỏ qua, và không nằm trong thanh tab. Đây là quyết định nghiệp
 * vụ 12/09/2026: mật khẩu do admin hoặc CLI đặt hộ là mật khẩu người khác
 * biết, và trên một hệ thống tính lương thì bản ghi chấm công của người đó
 * không còn chứng minh được ai bấm nút.
 *
 * Màn này cũng dùng lại được cho việc đổi mật khẩu tự nguyện ở mục Cá nhân —
 * khác nhau đúng một chỗ: `batBuoc` quyết định có nút "Để sau" hay không.
 *
 * Vì sao hiện quy tắc TRƯỚC khi người dùng gõ: server trả về danh sách lý do
 * rất cụ thể, nhưng để người ta gõ ba lần rồi mới biết luật là ba vòng gõ lại
 * trên bàn phím điện thoại giữa công trường.
 */

import { useState } from 'react'

import { ApiError } from '../api/loi'
import { doiMatKhau, nhanVienHienTai } from '../services/auth.service'

export default function DoiMatKhau({
  batBuoc = false, onXong, onHuy,
}: {
  batBuoc?: boolean
  onXong: () => void
  onHuy?: () => void
}) {
  const [cu, setCu] = useState('')
  const [moi, setMoi] = useState('')
  const [lapLai, setLapLai] = useState('')
  const [dangGui, setDangGui] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [lyDo, setLyDo] = useState<string[]>([])

  const nv = nhanVienHienTai()
  const khopNhau = moi.length > 0 && moi === lapLai

  const gui = async (e: React.FormEvent) => {
    e.preventDefault()
    setDangGui(true)
    setLoi(null)
    setLyDo([])
    try {
      await doiMatKhau(cu, moi)
      onXong()
    } catch (err) {
      if (err instanceof ApiError) {
        // Server trả CẢ danh sách lý do — hiện từng dòng, đừng gộp thành một
        // câu "không hợp lệ" rồi bắt người dùng đoán thiếu gì.
        const ds = (err.detail as { ly_do?: string[] })?.ly_do
        if (ds?.length) setLyDo(ds)
        const conLai = (err.detail as { con_lai?: number })?.con_lai
        setLoi(
          conLai !== undefined && conLai <= 3
            ? `${err.message}. Còn ${conLai} lần trước khi khoá tài khoản.`
            : err.message,
        )
      } else {
        setLoi(
          `Lỗi trong ứng dụng, không phải lỗi mạng: ${
            err instanceof Error ? err.message : String(err)
          }. Báo quản trị kèm nguyên văn dòng này.`,
        )
      }
      setDangGui(false)
    }
  }

  return (
    <div className="man-hinh dang-nhap">
      <div className="dang-nhap-tieu-de">
        <h1>Đổi mật khẩu</h1>
        {batBuoc ? (
          <p>
            Mật khẩu hiện tại do quản trị đặt hộ, nên có người khác biết. Đặt
            mật khẩu riêng của bạn trước khi chấm công.
          </p>
        ) : (
          <p>Đặt mật khẩu mới cho tài khoản {nv?.code ?? ''}.</p>
        )}
      </div>

      <form onSubmit={(e) => void gui(e)} className="form-dang-nhap">
        <label className="truong">
          <span>Mật khẩu hiện tại</span>
          <input
            type="password" value={cu} onChange={(e) => setCu(e.target.value)}
            autoComplete="current-password" required
          />
        </label>

        <label className="truong">
          <span>Mật khẩu mới</span>
          <input
            type="password" value={moi} onChange={(e) => setMoi(e.target.value)}
            autoComplete="new-password" required
          />
        </label>

        <label className="truong">
          <span>Gõ lại mật khẩu mới</span>
          <input
            type="password" value={lapLai} onChange={(e) => setLapLai(e.target.value)}
            autoComplete="new-password" required
          />
          {/* Gõ lại lệch nhau là lỗi client — bắt tại chỗ, đừng tốn một vòng
              gọi server để biết mình gõ nhầm. */}
          {lapLai.length > 0 && !khopNhau && (
            <small className="loi-dang-nhap">Hai lần gõ chưa giống nhau</small>
          )}
        </label>

        <div className="quy-tac-mat-khau">
          <strong>Mật khẩu cần:</strong>
          <ul>
            <li>Từ <strong>10 ký tự</strong> trở lên — hoặc từ 8 ký tự nếu có
              đủ 3 trong 4 nhóm: chữ thường, chữ HOA, chữ số, ký tự đặc biệt</li>
            <li>Không phải mật khẩu dễ đoán (<code>matkhau123</code>,
              <code>abc123</code>, tên công ty…)</li>
            <li>Không chứa tên hoặc mã nhân viên của chính bạn</li>
          </ul>
          <small>
            Một câu ngắn dễ nhớ như <code>Xoai-Chin-Muoi-7</code> vừa mạnh hơn
            vừa dễ gõ hơn <code>Abc@1234</code>.
          </small>
        </div>

        {lyDo.length > 0 && (
          <ul className="danh-sach-ly-do" role="alert">
            {lyDo.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        )}
        {loi && lyDo.length === 0 && (
          <p className="loi-dang-nhap" role="alert">{loi}</p>
        )}

        <button
          type="submit" className="nut-gui"
          disabled={dangGui || !cu || !khopNhau}
        >
          {dangGui ? 'Đang đổi…' : 'Đổi mật khẩu'}
        </button>

        {!batBuoc && onHuy && (
          <button type="button" className="nut-phu" onClick={onHuy}>
            Để sau
          </button>
        )}
      </form>

      {batBuoc && (
        <p className="ghi-chu-dang-nhap">
          Chưa đổi thì chưa chấm công được. Nếu bạn không nhớ mật khẩu hiện
          tại, nhờ quản lý đặt lại.
        </p>
      )}
    </div>
  )
}
